import React from "react";
import { Box, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

export default function GlassSearchBar({
  value,
  onChange,
  open,
  defaultOpen,
  onOpenChange,
  expandable = true,
  clearOnClose = true,
  showClearButton = true,
  placeholder = "Search...",
  ariaLabel = "Search",
  collapsedSize,
  height = 40,
  disabled = false,
  autoFocusDelay = 180,
  sx,
  inputSx,
}) {
  const inputRef = React.useRef(null);
  const controlSize = collapsedSize ?? height;
  const isOpenControlled = typeof open === "boolean";
  const [internalOpen, setInternalOpen] = React.useState(
    defaultOpen ?? !expandable,
  );
  const isValueControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState("");

  const isOpen = expandable
    ? isOpenControlled
      ? open
      : internalOpen
    : true;
  const searchValue = isValueControlled ? value : internalValue;

  React.useEffect(() => {
    if (!isOpen || disabled) return undefined;

    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, autoFocusDelay);

    return () => window.clearTimeout(focusTimer);
  }, [autoFocusDelay, disabled, isOpen]);

  const updateOpen = (nextOpen) => {
    if (!expandable) return;
    if (!isOpenControlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const updateValue = (nextValue) => {
    if (!isValueControlled) setInternalValue(nextValue);
    onChange?.(nextValue);
  };

  const clearValue = () => {
    updateValue("");
  };

  const closeSearch = () => {
    if (clearOnClose) clearValue();
    updateOpen(false);
  };

  const toggleSearch = () => {
    if (disabled) return;
    if (!expandable) {
      inputRef.current?.focus();
      return;
    }
    if (isOpen) {
      closeSearch();
      return;
    }
    updateOpen(true);
  };

  const handleClearClick = () => {
    if (expandable) {
      closeSearch();
      return;
    }
    clearValue();
    inputRef.current?.focus();
  };

  const rootSx = [
    {
      boxSizing: "border-box",
      width: isOpen ? "100%" : controlSize,
      height,
      minWidth: isOpen ? 0 : controlSize,
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      borderRadius: isOpen ? "12px" : "999px",
      border: isOpen
        ? "1px solid rgba(255,255,255,0.16)"
        : "1px solid rgba(255,255,255,0)",
      background: isOpen
        ? "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.06))"
        : "transparent",
      boxShadow: isOpen
        ? "inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 16px rgba(0,0,0,0.16)"
        : "none",
      backdropFilter: isOpen ? "blur(28px) saturate(112%)" : "none",
      WebkitBackdropFilter: isOpen ? "blur(28px) saturate(112%)" : "none",
      opacity: disabled ? 0.58 : 1,
      transition:
        "flex 260ms cubic-bezier(0.22, 1, 0.36, 1), width 260ms cubic-bezier(0.22, 1, 0.36, 1), min-width 260ms cubic-bezier(0.22, 1, 0.36, 1), background 180ms ease, border-color 180ms ease, box-shadow 180ms ease, opacity 180ms ease",
      "&:focus-within": {
        borderColor: "rgba(255,255,255,0.26)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.24), 0 10px 20px rgba(0,0,0,0.18)",
      },
    },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  return (
    <Box role="search" sx={rootSx}>
      <IconButton
        size="small"
        onClick={toggleSearch}
        disabled={disabled}
        aria-label={isOpen && expandable ? "Close search" : ariaLabel}
        sx={{
          width: controlSize,
          height: controlSize,
          flexShrink: 0,
          color: isOpen
            ? "rgba(234,231,226,0.88)"
            : "rgba(234,231,226,0.74)",
          p: 0,
          background: "transparent",
          "&:hover": {
            background: isOpen
              ? "rgba(255,255,255,0.1)"
              : "rgba(255,255,255,0.12)",
          },
        }}
      >
        <SearchIcon
          sx={{
            fontSize: 16,
            transform: isOpen ? "scale(0.94)" : "scale(1)",
            transition: "transform 180ms ease",
          }}
        />
      </IconButton>

      <Box
        component="input"
        ref={inputRef}
        value={searchValue}
        disabled={disabled}
        onChange={(event) => updateValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") closeSearch();
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        sx={[
          {
            width: isOpen ? "100%" : 0,
            minWidth: 0,
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            color: "rgba(255,255,255,0.92)",
            fontSize: 14,
            lineHeight: 1.2,
            pl: 0,
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? "auto" : "none",
            transition:
              "opacity 160ms ease 80ms, padding-left 220ms cubic-bezier(0.22, 1, 0.36, 1)",
            "&::placeholder": {
              color: "rgba(234,231,226,0.58)",
              opacity: 1,
            },
            "&:disabled": {
              WebkitTextFillColor: "rgba(255,255,255,0.62)",
            },
          },
          ...(Array.isArray(inputSx) ? inputSx : [inputSx]),
        ]}
      />

      {isOpen && showClearButton && (expandable || searchValue) ? (
        <IconButton
          size="small"
          onClick={handleClearClick}
          disabled={disabled}
          aria-label={expandable ? "Clear and close search" : "Clear search"}
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            color: "rgba(234,231,226,0.78)",
            p: 0,
            mr: 0.75,
            background: "transparent",
            "&:hover": {
              background: "rgba(255,255,255,0.14)",
              color: "#fff",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : null}
    </Box>
  );
}
